import { useState } from "react";
import { MicrositeEventType, ReasonType, transplantEventTypes } from "@nkr/enums/microsite-event-type";
import { MicrositeRecipientStatus } from '@nkr/enums/index';
import { Alert, Col, ControlLabel, FormControl, FormGroup, Row, Checkbox } from "react-bootstrap";
import useMicrositeLookupData from "@nkr/store/microsites/hooks/use-microsite-lookup-data";
import { NumberFilterModel } from "ag-grid-community";

type TProps = {
    eventType: number,
    reason: number,
    isEditMode: boolean,
    isBulk?: boolean,
    currentStatus?: number,
    contactDate?: string,
    contactMethod?: number,
    isCoachOptIn?: boolean,
    onEventTypeChange: (eventType: number) => void,
    onReasonChange: (firstLevel: number) => void,
    onContactDateChange?: (date: string) => void,
    onContactMethodChange?: (method: number) => void,
    onCoachOptInChange?: (optIn: boolean) => void
}

const Req = () => {
    return (<span style={{ color: 'darkred' }}>*</span>);
}

const EventTypePicker = ({ eventType, onEventTypeChange, reason, onReasonChange, isEditMode, isBulk, currentStatus, contactDate, onContactDateChange, contactMethod, onContactMethodChange, isCoachOptIn, onCoachOptInChange }: TProps) => {
    const { loading, error, eventTypes } = useMicrositeLookupData();
    const [reasonOther, setReasonOther] = useState<number>(-1);

    type TReasonType = {
        id: number,
        description: string,
        eventLabel?: string,
        eventTypes?: number[]
    };

    const reasonTypes: TReasonType[] = [
        {
            id: ReasonType.Transplanted,
            description: 'Transplanted',
            eventLabel: 'Transplanted Via',
            eventTypes: transplantEventTypes
        },
        {
            id: ReasonType.Deactivate,
            description: 'Deactivate'
        },
        {
            id: ReasonType.Contact,
            description: 'Contact'
        },
        {
            id: ReasonType.Reactivate,
            description: 'Reactivate',
            eventTypes: [MicrositeEventType.ReadyForTransplant]
        },
        {
            id: ReasonType.Other,
            description: 'Other'
        }
    ];

    const otherReasonTypes: TReasonType[] = [
        {
            id: 1,
            description: 'Health Change',
            eventLabel: 'Health Change',
            eventTypes: [MicrositeEventType.TooSick, MicrositeEventType.Died]
        },
        {
            id: 2,
            description: 'Moved to New Center',
            eventTypes: [MicrositeEventType.MovedNewCenter]
        },
        {
            id: 4,
            description: 'Duplicate Entry',
            eventTypes: [MicrositeEventType.DuplicateEntry]
        },
        {
            id: 5,
            description: 'Patient Opt-Out',
            eventLabel: 'Opt Out Reason',
            eventTypes: [MicrositeEventType.NeverAcceptedInvite, MicrositeEventType.NoLongerEnrolled, MicrositeEventType.PatientDeactivateRequest]
        }
    ];

    const contactReasonTypes: TReasonType[] = [
        {
            id: 1,
            description: 'Contact Attempted',
            eventTypes: [MicrositeEventType.ContactAttempted]
        },
        {
            id: 2,
            description: 'Contact Made',
            eventTypes: [MicrositeEventType.ContactMade]
        }
    ];

    //bulk only supports "Other", and a center transfer cannot be applied to a set of patients
    const isStagnant = (currentStatus == MicrositeRecipientStatus.Stagnant);

    const availableReasonTypes = isBulk
        ? reasonTypes.filter(t => t.id == ReasonType.Other)
        : isStagnant
            ? reasonTypes.filter(t => t.id == ReasonType.Transplanted
                || t.id == ReasonType.Deactivate
                || t.id == ReasonType.Contact
                || t.id == ReasonType.Reactivate)
            : reasonTypes;


    const availableOtherReasonTypes = isBulk
        ? otherReasonTypes.filter(t => !t.eventTypes?.some(e => e == MicrositeEventType.MovedNewCenter))
        : isStagnant
            ? otherReasonTypes.filter(t => !t.eventTypes.some(e => e == MicrositeEventType.ReadyForTransplant))
            : otherReasonTypes;

    const isContact = reason == ReasonType.Contact;
    const isDeactivate = reason == ReasonType.Deactivate;

    const secondLevelTypes = isContact ? contactReasonTypes : availableOtherReasonTypes;
    const secondLevelLabel = isContact ? 'Contact Update' : isDeactivate ? 'Deactivate Reason' : 'Other Reason';


    const reasonType = availableReasonTypes.find(l => l.id == reason);
    const reasonOtherType = secondLevelTypes.find((l) => l.id == reasonOther);

    const eventOptions = eventTypes.filter((f) =>
        reason > 0 && (
            reasonType?.eventTypes?.some(l => l == f.id)
            || reasonOtherType?.eventTypes?.some(l => l == f.id)
        ))
        .sort((a, b) => a.sortOrder - b.sortOrder)
        .map(t => <option key={t.id} value={t.id}>{t.description}</option>);


    const showSecondLevel = reasonType && !reasonType.eventTypes;


    const contactMethods = [
        { id: 1, description: 'Call' },
        { id: 2, description: 'Text' },
        { id: 3, description: 'Email' }
    ];

    const showContactFields = reason == ReasonType.Contact
        && (eventType == MicrositeEventType.ContactAttempted
            || eventType == MicrositeEventType.ContactMade);

    const handleReasonChange = (e: any) => {
        onReasonChange(e.target.value);
        const t = availableReasonTypes.find(l => l.id == e.target.value);
        onEventTypeChange(t?.eventTypes?.length == 1 ? t.eventTypes[0] : -1);
        setReasonOther(-1);
    };

    const handleReasonOtherChange = (e: any) => {
        setReasonOther(e.target.value);
        const reasonOtherType = secondLevelTypes.find((l) => l.id == e.target.value)
        if (reasonOtherType?.eventTypes?.length == 1) {
            onEventTypeChange(reasonOtherType.eventTypes[0]);
        } else {
            onEventTypeChange(-1);
        }
    }

    const handleEventChange = (e: any) => {
        onEventTypeChange(e.target.value);
    }

    if (reason > 0 && loading) {
        return (
            <Row>
                <Col sm={12} md={12}>Loading options ...</Col>
            </Row>
        );
    }

    if (error) {
        return (
            <Row>
                <Col sm={12} md={12}>
                    <Alert bsStyle="danger">An error occurred while loading change status reasons.</Alert>
                </Col>
            </Row>
        );
    }

    return (
        <>
            <Row>
                <Col sm={12} md={12}>
                    <FormGroup validationState={false ? 'error' : null}>
                        <ControlLabel>Reason <Req /></ControlLabel>
                        <FormControl
                            componentClass='select'
                            name='type'
                            value={reason}
                            onChange={handleReasonChange}
                            disabled={isEditMode || isBulk}
                        >
                            <option value={-1} disabled>- Select Reason -</option>
                            {availableReasonTypes.map(t => <option key={t.id} value={t.id}>{t.description}</option>)}
                        </FormControl>
                    </FormGroup>
                </Col>
            </Row>

            {showSecondLevel &&
                <Row>
                    <Col sm={12} md={12}>
                        <FormGroup validationState={false ? 'error' : null}>
                            <ControlLabel>{secondLevelLabel} <Req /></ControlLabel>
                            <FormControl
                                componentClass='select'
                                name='type'
                                value={reasonOther}
                                onChange={handleReasonOtherChange}>
                                <option value={-1} disabled>- Select {secondLevelLabel} -</option>
                                {secondLevelTypes.map(t =>
                                    <option key={t.id} value={t.id}>{t.description}</option>
                                )}
                            </FormControl>
                        </FormGroup>
                    </Col>
                </Row>
            }

            {eventOptions.length > 1 &&
                <Row>
                    <Col sm={12} md={12}>
                        <FormGroup validationState={false ? 'error' : null}>
                            <ControlLabel>{reasonType?.eventLabel ?? reasonOtherType?.eventLabel ?? 'Event'}  <Req /></ControlLabel>
                            <FormControl
                                componentClass='select'
                                name='type'
                                value={eventType}
                                onChange={handleEventChange}>
                                <option value={-1} disabled>- Select {reasonType?.eventLabel ?? reasonOtherType?.eventLabel ?? 'Event'} -</option>
                                {eventOptions}
                            </FormControl>
                        </FormGroup>

                    </Col>
                </Row>
            }

            {showContactFields &&
                <>
                    <Row>
                        <Col sm={12} md={12}>
                            <FormGroup>
                                <ControlLabel>
                                    {eventType == MicrositeEventType.ContactMade ? 'Date Contact Made' : 'Date Contact Attempted'} <Req />
                                </ControlLabel>
                                <FormControl
                                    type='date'
                                    name='contactDate'
                                    value={contactDate ?? ''}
                                    onChange={(e: any) => onContactDateChange?.(e.target.value)} />
                            </FormGroup>
                        </Col>
                    </Row>
                    <Row>
                        <Col sm={12} md={12}>
                            <FormGroup>
                                <ControlLabel>Contact Method <Req /></ControlLabel>
                                <FormControl
                                    componentClass='select'
                                    name='contactMethod'
                                    value={contactMethod ?? -1}
                                    onChange={(e: any) => onContactMethodChange?.(Number(e.target.value))}>
                                    <option value={-1} disabled>- Select Contact Method -</option>
                                    {contactMethods.map(m => <option key={m.id} value={m.id}>{m.description}</option>)}
                                </FormControl>
                            </FormGroup>
                        </Col>
                    </Row>
                    <Row>
                        <Col sm={12} md={12}>
                            <Checkbox
                                checked={isCoachOptIn ?? false}
                                onChange={(e: any) => onCoachOptInChange?.(e.target.checked)}>
                                Provide patient with a coach?
                            </Checkbox>
                        </Col>
                    </Row>
                </>
            }
        </>
    )
}

export default EventTypePicker